import {v4 as uuidv4} from 'uuid';
import {assertEvent, assign, setup} from 'xstate';

import {
  applyAddLayer,
  applyInitLayers,
  applyRemoveLayer,
  applySetActiveLayer,
  applyUpdateLayerName,
  buildInitialLayerContext,
  LayerBase,
  LayerContextBase,
  LayerMachineInputBase,
} from 'samples/plate-maps/layer-machine/sharedLayerActions';
import {PLATE_MAP_MAX_CONCENTRATION_LAYERS, PatternDirection} from 'samples/plate-maps/PlateMapConstants';
import {HydratedUnit} from 'units/util';

const MACHINE_NAME = 'ConcentrationLayerMachine';

export interface DilutionSettings {
  /** e.g., 2 for 1:2 dilution */
  dilutionFactor: number | null;
  dilutionDirection: PatternDirection;
  replicates: number | null;
  replicateDirection: PatternDirection;
}

export interface ConcentrationLayerData extends ConcentrationSettingsWithDilutionSettings, LayerBase {
  layerId: string;
  name: string;
}

export interface ConcentrationSettings {
  concentration: number | null;
  concentrationUnit: HydratedUnit | null;
  allowSetDilutionSettings: boolean;
}

export interface ConcentrationSettingsWithDilutionSettings extends ConcentrationSettings {
  dilutionSettings: DilutionSettings;
}

export type ConcentrationLayerEvents =
  | {type: 'initLayers'; layers: Array<ConcentrationLayerData>}
  | {type: 'addLayer'; layerId: string; name: string}
  | {type: 'removeLayer'; layerId: string}
  | {type: 'setActiveLayer'; layerId: string | null}
  | {type: 'updateLayerName'; layerId: string; name: string}
  | {
      type: 'updateConcentrationSettings';
      layerId: string;
      settings: Partial<ConcentrationSettings>;
    }
  | {
      type: 'updateLayerDilutionSettings';
      layerId: string;
      settings: Partial<DilutionSettings>;
    };

export type ConcentrationLayerContext = LayerContextBase<ConcentrationLayerData>;

export type ConcentrationLayerMachineInput = LayerMachineInputBase<ConcentrationLayerData>;

export function getDefaultDilutionSettings(): DilutionSettings {
  return {
    dilutionFactor: null,
    dilutionDirection: PatternDirection.RIGHT,
    replicates: null,
    replicateDirection: PatternDirection.RIGHT,
  };
}

export function createDefaultLayerSettings(layerId: string, name: string): ConcentrationLayerData {
  return {
    layerId,
    name,
    concentration: null,
    concentrationUnit: null,
    allowSetDilutionSettings: false,
    dilutionSettings: getDefaultDilutionSettings(),
  };
}

export function createConcentrationLayerId(): string {
  return `concentration-layer-${uuidv4()}`;
}

export const ConcentrationLayerMachine = setup({
  types: {
    input: {} as ConcentrationLayerMachineInput,
    context: {} as ConcentrationLayerContext,
    events: {} as ConcentrationLayerEvents,
  },
  actions: {
    initLayers: assign(({context, event}) => {
      assertEvent(event, 'initLayers');
      return applyInitLayers(context, event.layers, PLATE_MAP_MAX_CONCENTRATION_LAYERS, MACHINE_NAME);
    }),

    addLayer: assign(({context, event}) => {
      assertEvent(event, 'addLayer');
      const newLayer = createDefaultLayerSettings(event.layerId, event.name);
      return applyAddLayer(context, newLayer, PLATE_MAP_MAX_CONCENTRATION_LAYERS, MACHINE_NAME);
    }),

    removeLayer: assign(({context, event}) => {
      assertEvent(event, 'removeLayer');
      return applyRemoveLayer(context, event.layerId);
    }),

    setActiveLayer: assign(({context, event}) => {
      assertEvent(event, 'setActiveLayer');
      return applySetActiveLayer(context, event.layerId);
    }),

    updateLayerName: assign(({context, event}) => {
      assertEvent(event, 'updateLayerName');
      return applyUpdateLayerName(context, event.layerId, event.name);
    }),

    updateConcentrationSettings: assign(({context, event}) => {
      assertEvent(event, 'updateConcentrationSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer) {
        return {};
      }

      const updatedLayer: ConcentrationLayerData = {
        ...layer,
        ...(event.settings.concentration !== undefined && {concentration: event.settings.concentration}),
        ...(event.settings.concentrationUnit !== undefined && {
          concentrationUnit: event.settings.concentrationUnit,
        }),
        ...(event.settings.allowSetDilutionSettings !== undefined && {
          allowSetDilutionSettings: event.settings.allowSetDilutionSettings,
        }),
      };
      const layers = new Map(context.layers);
      layers.set(event.layerId, updatedLayer);
      return {...context, layers};
    }),

    updateLayerDilutionSettings: assign(({context, event}) => {
      assertEvent(event, 'updateLayerDilutionSettings');

      const layer = context.layers.get(event.layerId);
      if (!layer || !layer.allowSetDilutionSettings) {
        return {};
      }

      const updatedLayer: ConcentrationLayerData = {
        ...layer,
        dilutionSettings: {...layer.dilutionSettings, ...event.settings},
      };
      const layers = new Map(context.layers);
      layers.set(event.layerId, updatedLayer);
      return {...context, layers};
    }),
  },
}).createMachine({
  context: ({input}) => buildInitialLayerContext(input, PLATE_MAP_MAX_CONCENTRATION_LAYERS, MACHINE_NAME),

  id: 'ConcentrationLayer',
  initial: 'idle',

  states: {
    idle: {
      on: {
        initLayers: {
          actions: 'initLayers',
        },
        addLayer: {
          actions: 'addLayer',
        },
        removeLayer: {
          actions: 'removeLayer',
        },
        setActiveLayer: {
          actions: 'setActiveLayer',
        },
        updateLayerName: {
          actions: 'updateLayerName',
        },
        updateConcentrationSettings: {
          actions: 'updateConcentrationSettings',
        },
        updateLayerDilutionSettings: {
          actions: 'updateLayerDilutionSettings',
        },
      },
    },
  },
});
